import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { useCompactMode } from '../../context/CompactModeContext';
import './MessageList.css';

const tagColors = {
  urgent: '#ef4444',
  finance: '#10b981',
  legal: '#f59e0b',
  info: '#3b82f6',
  default: '#6366f1'
};

const getTagColor = (tag) => tagColors[tag.toLowerCase()] || tagColors.default;

const MessageList = ({
  messages,
  selectedFolder,
  onPreview,
  onTagFilter,
  compact,
  searchQuery = '',
  folders,
  onMoveMessage,
  selectedMessageIds = [],
  toggleSelectMessage,
}) => {


  const { compactMode } = useCompactMode();
  const [readStatus, setReadStatus] = useState({});
  const [starStatus, setStarStatus] = useState({});
  const [filteredTag, setFilteredTag] = useState('');

  const filteredMessages = messages.filter((msg) => {
    const matchesFolder =
      msg.folder?.toLowerCase() === selectedFolder?.toLowerCase();
    const senderName = msg.fromUserId?.name || "Unknown Sender";

    const matchesSearch =
      msg.subject?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      senderName.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesTag = filteredTag ? msg.tags?.includes(filteredTag) : true;
    return matchesFolder && matchesSearch && matchesTag;
  });

  const allTags = Array.from(new Set(messages.flatMap((m) => m.tags || [])));

  const toggleRead = (id) => {
    setReadStatus((prev) => {
      const newStatus = !prev[id];
      toast.info(`Marked as ${newStatus ? 'read' : 'unread'}`, { autoClose: 3000 });
      return { ...prev, [id]: newStatus };
    });
  };

  const toggleStar = (id) => {
    setStarStatus((prev) => {
      const newStatus = !prev[id];

      return { ...prev, [id]: newStatus };
    });
  };

  const highlightText = (text, query) => {
    if (!query) return text;
    const parts = text.split(new RegExp(`(${query})`, 'gi'));
    return parts.map((part, i) =>
      part.toLowerCase() === query.toLowerCase() ? <mark key={i}>{part}</mark> : part
    );
  };

  return (
    <div className={`message-list-container ${compactMode ? 'compact' : ''}`}>
      <div className="message-list-controls">
        <div className="tag-filters" role="group" aria-label="Filter messages by tag">

          {allTags.map((tag, i) => (
            <button
              key={i}
              className={filteredTag === tag ? 'active' : ''}
              onClick={() => {
                setFilteredTag(tag === filteredTag ? '' : tag);
                onTagFilter?.(tag);
              }}
            >
              #{tag}
            </button>
          ))}
        </div>
      </div>

      <ul className="message-list" role="list" aria-live="polite">
        {filteredMessages.map((msg) => (
          <li
            key={msg._id}
            className={`message-preview ${readStatus[msg._id] ? 'read' : 'unread'} ${compactMode ? 'compact' : ''}`}
            role="listitem"
          >

            <input
              type="checkbox"
              className="message-select-checkbox"
              checked={selectedMessageIds.includes(msg._id)}
              onChange={(e) => {
                e.stopPropagation();
                toggleSelectMessage?.(msg._id);
              }}
              onClick={(e) => e.stopPropagation()}
              aria-label={`Select ${msg.subject || "message"}`}
            />

            <div className="message-actions" role="group" aria-label="Message actions">
              <button
                className="action-btn"
                onClick={() => toggleStar(msg._id)}
                aria-label={starStatus[msg._id] ? 'Unstar message' : 'Star message'}
                title={starStatus[msg._id] ? 'Unstar' : 'Star'}
              >
                {starStatus[msg._id] ? '⭐' : '☆'}
              </button>

              <button
                className="action-btn"
                onClick={() => toggleRead(msg._id)}
                aria-label={readStatus[msg._id] ? 'Mark as unread' : 'Mark as read'}
                title={readStatus[msg._id] ? 'Mark Unread' : 'Mark Read'}
              >
                {readStatus[msg._id] ? '✉️' : '📭'}
              </button>


              <div
                className="message-header"
                tabIndex="0"
                role="button"
                aria-pressed="false"
                onClick={() => onPreview?.(msg)}
                onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onPreview?.(msg)}
              >
                <strong>{highlightText(msg.subject || 'No Subject', searchQuery)}</strong>
                <span className="message-meta">From: {highlightText(msg.fromUserId?.name || "Unknown Sender", searchQuery)}</span>
              </div>

              {msg.tags?.length > 0 && (
                <div className="message-tags" aria-label="Tags">
                  {msg.tags.map((tag, index) => (
                    <span
                      key={index}
                      className={`tag-chip ${compactMode ? 'compact' : ''}`}
                      style={{ backgroundColor: getTagColor(tag) }}
                      onClick={() => {
                        setFilteredTag(tag === filteredTag ? '' : tag);
                        onTagFilter?.(tag);
                      }}
                      title={`Filter by #${tag}`}
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </li>

        ))}
      </ul>

      {filteredMessages.length === 0 && (
        <p className="empty-state" role="status">
          📭 No messages in this folder.
        </p>
      )}
    </div>
  );
};

export default MessageList;
